import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  BlogPost,
  BlogFilters,
  FetchBlogParams,
  BlogCategory,
  BlogTag,
  BlogLocale,
} from '../types/blog';
import { Profile } from '../types/profile';
import imageCompression from 'browser-image-compression';

/** Prefix for blog post images in the `blog_assets` bucket (matches uploads). */
export const BLOG_POST_IMAGES_PREFIX = 'post-images';

export type BlogPostImageItem = {
  path: string;
  publicUrl: string;
  fileName: string;
};

const BLOG_IMAGE_FILENAME_PATTERN = /\.(jpe?g|png|gif|webp|svg)$/i;

interface BlogStore {
  posts: BlogPost[];
  categories: BlogCategory[];
  tags: BlogTag[];
  filters: BlogFilters;
  offset: number;
  limit: number;
  hasMore: boolean;
  loading: boolean;

  setFilters: (filters: BlogFilters) => void;
  fetchPosts: (params?: FetchBlogParams) => Promise<void>;
  fetchMorePosts: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchTags: () => Promise<void>;
  getPostById: (id: string) => Promise<BlogPost | null>;
  getPostIdByLocaleInGroup: (
    translationGroupId: string,
    locale: BlogLocale
  ) => Promise<string | null>;
  createPost: (
    post: Partial<BlogPost>,
    categoryIds?: string[],
    tagIds?: string[]
  ) => Promise<BlogPost | null>;
  updatePost: (
    id: string,
    updates: Partial<BlogPost>,
    categoryIds?: string[],
    tagIds?: string[]
  ) => Promise<void>;
  deletePost: (id: string) => Promise<void>;

  // Author management
  authors: Profile[];
  fetchAuthors: () => Promise<void>;

  // Category CRUD
  createCategory: (category: Partial<BlogCategory>) => Promise<BlogCategory | null>;
  updateCategory: (id: string, updates: Partial<BlogCategory>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  // Tag CRUD
  createTag: (tag: Partial<BlogTag>) => Promise<BlogTag | null>;
  updateTag: (id: string, updates: Partial<BlogTag>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;

  uploadImage: (file: File) => Promise<string | null>;
  listBlogPostImages: () => Promise<BlogPostImageItem[]>;
}

const DEFAULT_LIMIT = 10;
type BlogPostMutationPayload = Partial<BlogPost> & { author?: unknown };

const sanitizeBlogPostPayload = (post: BlogPostMutationPayload) => {
  const dbPost = { ...post };
  delete dbPost.categories;
  delete dbPost.tags;
  delete dbPost.author_name;
  delete dbPost.author;

  return dbPost;
};

export const useBlogStore = create<BlogStore>((set, get) => ({
  posts: [],
  categories: [],
  tags: [],
  authors: [],
  filters: { status: '', category: '', search: '', locale: '' },
  offset: 0,
  limit: DEFAULT_LIMIT,
  hasMore: true,
  loading: false,

  setFilters: filters => set({ filters }),

  fetchPosts: async (params = {}) => {
    try {
      set({ loading: true });
      const { limitCount, offsetCount, isLoadMore = false } = params;
      const limit = limitCount || DEFAULT_LIMIT;
      const offset = offsetCount || 0;

      const activeFilters = params.filters || get().filters;

      let query = supabase.from('blog_posts').select(
        `
          *,
          categories:blog_posts_categories(blog_categories(*)),
          tags:blog_posts_tags(blog_tags(*))
        `,
        { count: 'exact' }
      );

      if (activeFilters.status) query = query.eq('status', activeFilters.status);
      if (activeFilters.category) query = query.eq('category', activeFilters.category);
      if (activeFilters.locale) query = query.eq('locale', activeFilters.locale);
      if (activeFilters.search) {
        query = query.ilike('title', `%${activeFilters.search}%`);
      }

      const { data, error, count } = await query
        .order('published_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      // Fetch author names separately
      const authorIds = [
        ...new Set((data || []).map((p: { author_id?: string }) => p.author_id).filter(Boolean)),
      ] as string[];
      const authorMap: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', authorIds);
        (profiles || []).forEach((p: { id: string; first_name: string; last_name: string }) => {
          authorMap[p.id] = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Pigeons';
        });
      }

      const formattedData = data?.map(post => ({
        ...post,
        categories:
          post.categories?.map((c: { blog_categories: unknown }) => c.blog_categories) || [],
        tags: post.tags?.map((t: { blog_tags: unknown }) => t.blog_tags) || [],
        author_name: post.author_id ? authorMap[post.author_id] || 'Pigeons' : 'Pigeons',
      })) as BlogPost[];

      set(state => ({
        posts: isLoadMore ? [...state.posts, ...formattedData] : formattedData,
        offset: offset + limit,
        hasMore: (count || 0) > offset + limit,
      }));
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      set({ loading: false });
    }
  },

  fetchMorePosts: async () => {
    const { offset, fetchPosts } = get();
    await fetchPosts({ offsetCount: offset, isLoadMore: true });
  },

  fetchCategories: async () => {
    const { data, error } = await supabase.from('blog_categories').select('*').order('name');
    if (!error) set({ categories: data });
  },

  fetchTags: async () => {
    const { data, error } = await supabase.from('blog_tags').select('*').order('name');
    if (!error) set({ tags: data });
  },

  fetchAuthors: async () => {
    const { data, error } = await supabase.rpc('admin_fetch_blog_authors');
    if (!error) set({ authors: data as Profile[] });
  },

  getPostIdByLocaleInGroup: async (translationGroupId: string, locale: BlogLocale) => {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('id')
      .eq('translation_group_id', translationGroupId)
      .eq('locale', locale)
      .maybeSingle();
    if (error) {
      console.error('Error resolving sibling post:', error);
      return null;
    }
    return data?.id ?? null;
  },

  getPostById: async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(
          `
          *,
          categories:blog_posts_categories(blog_categories(*)),
          tags:blog_posts_tags(blog_tags(*))
        `
        )
        .eq('id', id)
        .single();

      if (error) throw error;

      let authorName = 'Pigeons';
      if (data.author_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', data.author_id)
          .single();
        if (profile)
          authorName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Pigeons';
      }

      return {
        ...data,
        categories:
          data.categories?.map((c: { blog_categories: unknown }) => c.blog_categories) || [],
        tags: data.tags?.map((t: { blog_tags: unknown }) => t.blog_tags) || [],
        author_name: authorName,
      } as BlogPost;
    } catch (error) {
      console.error('Error fetching post by id:', error);
      return null;
    }
  },

  createPost: async (post, categoryIds = [], tagIds = []) => {
    try {
      // Strip out non-DB fields; never send `id` so the DB generates a new row (avoids reusing a
      // stale id when the editor navigates from /blog/:id to /blog/new without remounting).
      const dbPost = sanitizeBlogPostPayload(post as BlogPostMutationPayload);
      delete dbPost.id;

      const { data, error } = await supabase.from('blog_posts').insert([dbPost]).select().single();

      if (error) throw error;

      const postId = data.id;

      // Insert relationships
      if (categoryIds.length > 0) {
        await supabase
          .from('blog_posts_categories')
          .insert(categoryIds.map(id => ({ post_id: postId, category_id: id })));
      }
      if (tagIds.length > 0) {
        await supabase
          .from('blog_posts_tags')
          .insert(tagIds.map(id => ({ post_id: postId, tag_id: id })));
      }

      set(state => ({ posts: [data, ...state.posts] }));
      return data;
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error; // Re-throw so the editor can catch and display error toast
    }
  },

  updatePost: async (id, updates, categoryIds = [], tagIds = []) => {
    try {
      // Strip out non-DB fields that come from joins/formatting
      const dbUpdates = sanitizeBlogPostPayload(updates as BlogPostMutationPayload);

      const { error } = await supabase.from('blog_posts').update(dbUpdates).eq('id', id);

      if (error) throw error;

      // Update relationships (clear and re-insert logic)
      await supabase.from('blog_posts_categories').delete().eq('post_id', id);
      if (categoryIds.length > 0) {
        await supabase
          .from('blog_posts_categories')
          .insert(categoryIds.map(catId => ({ post_id: id, category_id: catId })));
      }

      await supabase.from('blog_posts_tags').delete().eq('post_id', id);
      if (tagIds.length > 0) {
        await supabase
          .from('blog_posts_tags')
          .insert(tagIds.map(tagId => ({ post_id: id, tag_id: tagId })));
      }

      set(state => ({
        posts: state.posts.map(p => (p.id === id ? { ...p, ...updates } : p)),
      }));
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error; // Re-throw so the editor can catch and display error toast
    }
  },

  deletePost: async id => {
    try {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);

      if (error) throw error;

      set(state => ({
        posts: state.posts.filter(p => p.id !== id),
      }));
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  },

  // Category CRUD
  createCategory: async category => {
    const { data, error } = await supabase
      .from('blog_categories')
      .insert([category])
      .select()
      .single();
    if (error) {
      console.error('Error creating category:', error);
      throw error;
    }
    set(state => ({ categories: [...state.categories, data] }));
    return data;
  },
  updateCategory: async (id, updates) => {
    const { error } = await supabase.from('blog_categories').update(updates).eq('id', id);
    if (error) {
      console.error('Error updating category:', error);
      throw error;
    }
    set(state => ({
      categories: state.categories.map(c => (c.id === id ? { ...c, ...updates } : c)),
    }));
  },
  deleteCategory: async id => {
    const { error } = await supabase.from('blog_categories').delete().eq('id', id);
    if (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
    set(state => ({ categories: state.categories.filter(c => c.id !== id) }));
  },

  // Tag CRUD
  createTag: async tag => {
    const { data, error } = await supabase.from('blog_tags').insert([tag]).select().single();
    if (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
    set(state => ({ tags: [...state.tags, data] }));
    return data;
  },
  updateTag: async (id, updates) => {
    const { error } = await supabase.from('blog_tags').update(updates).eq('id', id);
    if (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
    set(state => ({ tags: state.tags.map(t => (t.id === id ? { ...t, ...updates } : t)) }));
  },
  deleteTag: async id => {
    const { error } = await supabase.from('blog_tags').delete().eq('id', id);
    if (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
    set(state => ({ tags: state.tags.filter(t => t.id !== id) }));
  },

  uploadImage: async (file: File) => {
    try {
      // 1. Image compression in the background
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
        initialQuality: 0.8,
      };

      const compressedFile = await imageCompression(file, options);

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${BLOG_POST_IMAGES_PREFIX}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog_assets')
        .upload(filePath, compressedFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('blog_assets').getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  listBlogPostImages: async () => {
    const { data, error } = await supabase.storage
      .from('blog_assets')
      .list(BLOG_POST_IMAGES_PREFIX, {
        limit: 200,
        offset: 0,
        sortBy: { column: 'created_at', order: 'desc' },
      });

    if (error) throw error;

    const files = (data ?? []).filter(
      f => Boolean(f.name) && !f.name.includes('/') && BLOG_IMAGE_FILENAME_PATTERN.test(f.name)
    );

    return files.map(f => {
      const path = `${BLOG_POST_IMAGES_PREFIX}/${f.name}`;
      const { data: urlData } = supabase.storage.from('blog_assets').getPublicUrl(path);
      return {
        path,
        publicUrl: urlData.publicUrl,
        fileName: f.name,
      };
    });
  },
}));
